import React, { useState, useCallback } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useDropzone } from 'react-dropzone';
import { Helmet } from 'react-helmet-async';
import ProcessingProgress from './common/ProcessingProgress';
import useProcessingProgress from '../hooks/useProcessingProgress';
import '../styles/components.css';

const TextCaseStandardizer = () => {
  const [file, setFile] = useState(null);
  const [caseType, setCaseType] = useState('title');
  const [applyTo, setApplyTo] = useState('all');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [error, setError] = useState('');
  const [availableColumns, setAvailableColumns] = useState([]);
  
  const {
    progress,
    status,
    isComplete,
    isLoading,
    simulateProgress
  } = useProcessingProgress();

  const processingStages = [
    { progress: 0, status: 'Starting standardization process...', duration: 800 },
    { progress: 20, status: 'Loading workbook...', duration: 1000 },
    { progress: 40, status: 'Analyzing content...', duration: 1200 },
    { progress: 70, status: 'Applying text case changes...', duration: 1500 },
    { progress: 90, status: 'Preparing final workbook...', duration: 1000 },
    { progress: 100, status: 'Complete!', duration: 500 }
  ];

  const onDrop = useCallback(async (acceptedFiles) => {
    setError('');
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    if (!file.name.match(/\.(xlsx|xls|xlsm)$/i)) {
      setError('Please upload a valid Excel file (.xlsx, .xls, or .xlsm)');
      return;
    }
    
    setFile(file);
    
    try {
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      
      const worksheet = workbook.worksheets[0];
      const columns = worksheet.getRow(1).values.slice(1).map((col, index) => ({
        label: col || `Column ${index + 1}`,
        value: index + 1
      }));
      
      setAvailableColumns(columns);
    } catch (err) {
      setError('Error analyzing file: ' + err.message);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false
  });

  const standardizeCase = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setError('');
    
    try {
      // Start progress simulation and wait for it to initialize
      await simulateProgress(processingStages);
      
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      
      workbook.worksheets.forEach(worksheet => {
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header row
          
          row.eachCell((cell, colNumber) => {
            if (cell.value && typeof cell.value === 'string') {
              if (applyTo === 'all' || 
                 (applyTo === 'selected' && selectedColumns.includes(colNumber))) {
                
                let newValue = cell.value;
                switch (caseType) {
                  case 'upper':
                    newValue = cell.value.toUpperCase();
                    break;
                  case 'lower':
                    newValue = cell.value.toLowerCase();
                    break;
                  case 'title':
                    newValue = cell.value
                      .toLowerCase()
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');
                    break;
                  case 'sentence':
                    newValue = cell.value.charAt(0).toUpperCase() + 
                             cell.value.slice(1).toLowerCase();
                    break;
                  default:
                    // If an unknown case type is provided, keep the original value
                    newValue = cell.value;
                    break;
                }
                cell.value = newValue;
              }
            }
          });
        });
      });

      const outputBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([outputBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      saveAs(blob, `standardized_${file.name}`);
      
    } catch (err) {
      setError('Error processing file: ' + err.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Text Case Standardizer - Excel Text Formatting Tool | Excel Toolbox</title>
        <meta name="description" content="Free tool to standardize text case in Excel files. Convert text to Title Case, UPPERCASE, lowercase, or Sentence case. Process Excel files securely on your device." />
        <meta name="keywords" content="excel text case, standardize excel text, excel text formatter, excel case converter" />
        <meta property="og:title" content="Text Case Standardizer - Excel Text Formatting Tool" />
        <meta property="og:description" content="Free tool to standardize text case in Excel files. Convert text to various cases while maintaining data integrity." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://exceltoolbox.org/text-case-standardizer" />
      </Helmet>
      <Container className="tool-container">
        <Card className="tool-card">
          <Card.Header className="tool-header" as="h5">Text Case Standardizer</Card.Header>
          <Card.Body className="tool-body">
            <Card.Text className="mb-4">
              Transform and standardize text case in your Excel workbooks. This tool helps maintain consistency by converting text to your chosen format across all or selected columns.
              <br /><br />
              <strong>How it works:</strong>
              <ol className="mb-3">
                <li>Upload your Excel file</li>
                <li>Choose your preferred case format (Title Case, UPPER CASE, lower case, or Sentence case)</li>
                <li>Select whether to apply changes to all text columns or specific ones</li>
                <li>Click 'Standardize Text Case' to process your file</li>
              </ol>
              <strong>Note:</strong> Non-text data (numbers, dates, formulas) will remain unchanged.
            </Card.Text>

            <div className="dropzone-container">
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <div className={`dropzone ${isDragActive ? 'active' : ''}`}>
                  <i className="bi bi-cloud-upload dropzone-icon"></i>
                  {file ? (
                    <p className="dropzone-text">Selected file: {file.name}</p>
                  ) : (
                    <p className="dropzone-text">
                      {isDragActive ? 'Drop the file here' : 'Drag and drop an Excel file here, or click to select'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {file && (
              <Alert variant="success" className="mb-3">
                <i className="bi bi-file-earmark-check me-2"></i>
                File loaded: {file.name}
              </Alert>
            )}

            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Case Type</Form.Label>
                    <Form.Select 
                      value={caseType}
                      onChange={(e) => setCaseType(e.target.value)}
                      className="form-select-custom"
                    >
                      <option value="title">Title Case (First Letter Each Word)</option>
                      <option value="upper">UPPER CASE</option>
                      <option value="lower">lower case</option>
                      <option value="sentence">Sentence case</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Apply To</Form.Label>
                    <Form.Select 
                      value={applyTo}
                      onChange={(e) => setApplyTo(e.target.value)}
                      className="form-select-custom"
                    >
                      <option value="all">All Text Columns</option>
                      <option value="selected">Selected Columns</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {applyTo === 'selected' && availableColumns.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Select Columns</Form.Label>
                  <div className="column-selector">
                    {availableColumns.map((col) => (
                      <Form.Check
                        key={col.value}
                        type="checkbox"
                        label={col.label}
                        checked={selectedColumns.includes(col.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedColumns([...selectedColumns, col.value]);
                          } else {
                            setSelectedColumns(selectedColumns.filter(c => c !== col.value));
                          }
                        }}
                        className="form-check-custom"
                      />
                    ))}
                  </div>
                </Form.Group>
              )}

              {error && (
                <Alert variant="danger" className="mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

            <ProcessingProgress
              progress={progress}
              status={status}
              isComplete={isComplete}
              isLoading={isLoading}
              completionMessage="Text has been standardized successfully!"
            />

              <Button 
                variant="success" 
                onClick={standardizeCase}
                disabled={!file || isLoading}
                className="action-button"
              >              
                Standardize Text Case
              </Button>
            </Form>
         
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default TextCaseStandardizer;
